import React, { useState } from 'react';
import { injectIntl } from 'react-intl';
import { Route, HashRouter as Router, Switch } from 'react-router-dom';
import { i18nIntl,migrate_question } from '../../lib/ep.js';

import ActionButton from '../builder/ActionButton.js';
import BuilderCanvas from '../builder/BuilderCanvas.js';
import BuilderMain from '../builder/BuilderMain.js';
import BuilderSidebar from '../builder/BuilderSidebar.js';
import BuilderSubSectionTitle from './BuilderSubSectionTitle.js';
import ButtonCollection from '../builder/ButtonCollection.js';
import ColorPicker from '../builder/ColorPicker.js';
import { AutoPostImageUploader } from '../builder/ImageUploader.js';
import Model from '../builder/Model.js';
import { NavigationTabsBuilder } from '../builder/NavigationTabs.js';
import Range from '../builder/Range.js';
import Select from '../builder/Select.js';

import { GameContainerWrapper } from '../front/GameContainer.js';
import Layout from '../front/Layout.js';
import Message from '../builder/Message.js';
import MineSweeperClient from '../front/MineSweeperClient.js';
import Checkbox from '../builder/Checkbox.js';
import GameInstructionsBuilder from './GameInstructionsBuilder.js';
import GameScoreBuilder from './GameScoreBuilder.js';
import GameSoundtrackFields from '../builder/GameSoundtrackFields.js';

const GameSettings = ({ stage, change, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const [ errors_game, setErrorsGame ] = useState({});

	const getMinAttemps = () => {
		const level = stage?.puzzle_level ?? 'easy';

		switch(level) {
			case 'hard':
				return 5;

			case 'normal':
				return 4;

			case 'easy':
				return 3;
		}

		return 3;
	};

	const option_size = [
		{ k:'small', v:i18n("Small") + " (6 × 6)" },
		{ k:'medium', v:i18n("Medium") + " (10 × 10)" },
		{ k:'large', v:i18n("Large") + " (14 × 14)" },
	];


	const grid = stage?.minesweeper_grid ?? 'medium';
	let columns;
	switch(grid) {
		case 'small': columns = 6;
			break;

		case 'large':
			columns = 14;
			break;

		case 'medium':
		default:
			columns = 10;
			break;
	}

	const max_per_mine = 0.25; //maximum percentage of mines 25/100
	const min_per_mine = 0.10; //minimum percentage of mines 10/100
	const max_mine = Math.pow(columns,2) * max_per_mine;
	const min_mine = Math.floor(Math.pow(columns,2) * min_per_mine);
	let level_warning_label = "";
	let level_warning_icon = "";
	let minesweep_level = "";
	
	const percentage_level = Math.floor(stage?.minesweeper_mines / max_mine * 100);

	if(percentage_level <= 35) {
		level_warning_icon = 'knk-level-easy';
		minesweep_level = "Low";
	} else if(percentage_level <= 71) {
		level_warning_icon = 'knk-level-medium';
		minesweep_level = "Medium";
	} else {
		level_warning_icon = 'knk-level-hard';
		minesweep_level = "High";
	}
	level_warning_label = props.intl.formatMessage({id:"The difficulty level is {difficulty}"}, { difficulty:i18n(minesweep_level) });

	return (
		<>
			<BuilderSidebar
				onSave={props.saveStage}
				errors={(Object.keys(errors_game).length > 0) ? true : false}
			>
				<NavigationTabsBuilder
					relative_selection={true}
					active={`${props.nav_active}`}
					sections={props?.nav_sections ?? []}
				>{props.stage_indicator}</NavigationTabsBuilder>

				<article>
					<Model model={stage} change={change}>
						<Select
							_class={{ size:50, first:true }}
							name="minesweeper_grid"
							values={option_size}
						>Grid size</Select>

						<Range
							min={30}
							max={300}
							step={5}
							label_props={{
								addition:"In seconds",
							}}
							name="game_time"
							value={Math.max((stage?.game_time ?? 60), 30)}
						>Time limit</Range>

						<Range
							_class={{size:60}}
							min={min_mine}
							max={max_mine}
							step={1}
							name="minesweeper_mines"
							value={stage?.minesweeper_mines ?? 10}
						>Number of mines</Range>
					</Model>

					<Message
						type='blue'
						icon={level_warning_icon}
						title={level_warning_label}
						_class={{ other:'in_bottom' }}
					/>

					<Model model={stage} change={change}>
						<Checkbox
							name='silence'
						>Disable sounds</Checkbox>
					</Model>

					<GameSoundtrackFields
						stage={stage}
						change={change}
					/>
				</article>
			</BuilderSidebar>

			<BuilderCanvas fit_to_window="content">
				<Layout
					promo={props.promotion}
					is_builder={true}
					page="quiz"
					menus={props.promoModel.Custom__Menus}
					layout_extra_classes="is_new_game"
				>
					<div style={{width:'100%'}}>
						{(Object.keys(errors_game).length == 0) ? (
							<MineSweeperClient
								stage={{ Stage:stage }}
								is_builder={true}
								is_playable={true}
								section="settings"
							/>
						) : (
							<GameContainerWrapper>
								<div class="is_fullscreen relative_to_container empty_game_warning not_awesome orangeBg knk-arrow-l-big">
									{Object.values(errors_game).map((error, i) => {
										if(typeof error === 'object') return null;

										return (
											<p key={`error${i}`}>{error}</p>
										)
									})}
								</div>
							</GameContainerWrapper>
						)}
					</div>
				</Layout>
			</BuilderCanvas>
		</>
	)
};

const GameDesign = ({ stage, change, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	return (
		<>
			<BuilderSidebar
				onSave={props.saveStage}
			>
				<NavigationTabsBuilder
					relative_selection={true}
					active={`${props.nav_active}`}
					sections={props?.nav_sections ?? []}
				>{props.stage_indicator}</NavigationTabsBuilder>

				<article>
					<BuilderSubSectionTitle
						title="Mined cell"
						icon='knk-bomb'
					/>

					<Model model={stage} change={change}>
						<ColorPicker
							name="minesweeper_bomb_color"
							color={stage?.minesweeper_bomb_color ?? 'rgba(215, 35, 60, 1)'}
							_class={{ size:50 }}
						>Background color</ColorPicker>

						<AutoPostImageUploader
							auto_url={`/promotions/auto_img/${props.promotion.id}`}
							auto_type='stages'
							name='minesweeper_bomb_image'
							image={(stage.minesweeper_bomb_image) ? stage.full_minesweeper_bomb_image : ''}
							small_input={true}
							_size={{width:250, height:250}}
							display_custom_dragging_instructions="Upload the image"
							_class={{ size:50 }}
						>Icon</AutoPostImageUploader>
					</Model>
				</article>

				<article class="gui_not_in_top_huge">
					<BuilderSubSectionTitle
						title="Flagged cell"
						icon='knk-flag'
					/>

					<Model model={stage} change={change}>
						<ColorPicker
							name="minesweeper_flag_color"
							color={stage?.minesweeper_flag_color ?? 'rgba(120, 195, 0, 1)'}
							_class={{ size:50 }}
						>Background color</ColorPicker>

						<AutoPostImageUploader
							auto_url={`/promotions/auto_img/${props.promotion.id}`}
							auto_type='stages'
							name='minesweeper_flag_image'
							image={(stage.minesweeper_flag_image) ? stage.full_minesweeper_flag_image : ''}
							small_input={true}
							_size={{width:250, height:250}}
							display_custom_dragging_instructions="Upload the image"
							_class={{ size:50 }}
						>Icon</AutoPostImageUploader>
					</Model>
				</article>

				<article class="gui_not_in_top_huge">
					<BuilderSubSectionTitle
						title="Board"
						icon='knk-page'
					/>

					<Model model={stage} change={change}>
						<ColorPicker
							name="minesweeper_grid_color"
							color={stage?.minesweeper_grid_color ?? 'rgba(0, 0, 0, 1)'}
							_class={{ size:50 }}
						>Grid lines color</ColorPicker>

						<ColorPicker
							name="minesweeper_cell_color"
							color={stage?.minesweeper_cell_color ?? 'rgba(120, 120, 120, 1)'}
							_class={{ size:50 }}
						>Closed cell color</ColorPicker>

						<ColorPicker
							name="text_color"
							color={stage?.text_color ?? 'rgba(0, 0, 0, 1)'}
							_class={{ size:50 }}
						>Text color</ColorPicker>

						<ColorPicker
							name="bg_color"
							color={stage?.bg_color ?? 'rgba(255, 255, 255, 1)'}
							_class={{ size:50 }}
						>Background color</ColorPicker>
					</Model>
				</article>
			</BuilderSidebar>

			<BuilderCanvas fit_to_window="content">
				<Layout
					promo={props.promotion}
					is_builder={true}
					page="quiz"
					menus={props.promoModel.Custom__Menus}
					layout_extra_classes="is_new_game"
				>
					<div style={{width:'100%'}}>
						<MineSweeperClient
							stage={{ Stage:stage }}
							is_builder={true}
							is_playable={true}
							section="settings"
						/>
					</div>
				</Layout>
			</BuilderCanvas>
		</>
	)
};

const GameTest = ({ stage, change, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const restart = () => {
		change('reset', (stage.reset != undefined) ? (stage.reset + 1) : 0);
	};

	return (
		<>
			<BuilderSidebar
				onSave={props.saveStage}
			>
				<NavigationTabsBuilder
					relative_selection={true}
					active={`${props.nav_active}`}
					sections={props?.nav_sections ?? []}
				>{props.stage_indicator}</NavigationTabsBuilder>

				<ButtonCollection>
					<ActionButton
						icon='knk-reload'
						onClick={() => restart()}
					>Restart game</ActionButton>
				</ButtonCollection>
			</BuilderSidebar>

			<BuilderCanvas
				fit_to_window="content"
				off_content={<>
					<Message
						_class={{ other:'in_top in_bottom' }}
						title={false}
					>
						<p>{i18n("There's a black dot in the middle of the game board. If it turns red, it means that there's a bomb in the cell you're on.")} <strong>{i18n("This dot does not appear for users.")}</strong></p>
					</Message>
				</>}
			>
				<Layout
					promo={props.promotion}
					is_builder={true}
					page="quiz"
					menus={props.promoModel.Custom__Menus}
					layout_extra_classes="is_new_game"
				>
					<div style={{width:'100%'}}>
						<MineSweeperClient
							stage={{ Stage:stage }}
							is_builder={true}
							is_playable={true}
							is_test={true}
						/>
					</div>
				</Layout>
			</BuilderCanvas>
		</>
	)
};

const MineSweeperBuilder = ({ base_route, ...props }) => {
	const basename = `/stages/${props.stage.id}/minesweeper`;
	const nav_sections = [
		{ label:"Go back", link:`/stages/${props.stage.id}/options`, icon:'knk-go-prev' },
		{ label:"Gameplay settings", link:`${basename}/settings`, icon:'knk-settings' },
		{ label:"Design", link:`${basename}/design`, icon:'knk-artist-mode' },
		{ label:"Instructions", link:`${basename}/instructions`, icon:'knk-help' },
		{ label:"Score screen", link:`${basename}/gamescore`, icon:'knk-like' },
		{ label:"Test the game", link:`${basename}/test`, icon:'knk-ok' },
	];

	const stage = props.stage;

	const migrate_from_question = true;
	if(migrate_from_question) {
		const question = props.questionStore.getState()?.questions.questions.find((q) => (q.Question.stage == props.stage.id));
		const game_stage_fields_map = {
			grid:'minesweeper_grid',
			mines:'minesweeper_mines',
			time_seconds:'game_time',
			attempts:'game_attempts',
			cell_color:'bg_color',
			color_ok:'color_ok',
			color_ko:'color_ko',
			mine_img:'minesweeper_bomb_image',
			full_mine_img:'full_minesweeper_bomb_image',
			flag_img:'minesweeper_flag_image',
			full_flag_img:'full_minesweeper_flag_image',
		};

		if(question) migrate_question(game_stage_fields_map, stage, question.Question);
	};
	

	const common_props = {
		stage,
		change:props.change,
		changeMulti:props.changeMulti,
		intl:props.intl,
		promotion:props.promotion,
		promoModel:props.promoModel,
		saveStage:props.saveStage,
		stage_indicator:props.stage_indicator,
		nav_sections:nav_sections,
	};

	return (
		<BuilderMain pm={props.promoModel}>
			<Router basename={basename} hashType="noslash">
				<Switch>
					<Route exact={true} path="/settings">
						<GameSettings
							{...common_props}
							nav_active={`/settings`}
						/>
					</Route>

					<Route exact={true} path="/design">
						<GameDesign
							{...common_props}
							nav_active={`/design`}
						/>
					</Route>

					<Route exact={true} path="/instructions">
						<GameInstructionsBuilder
							{...common_props}
							nav_active={`/instructions`}
						>
							<MineSweeperClient
								stage={{ Stage:props.stage }}
								promotion={props.promotion}
								is_builder={true}
								section="instructions"
							/>
						</GameInstructionsBuilder>
					</Route>

					<Route exact={true} path="/gamescore">
						<GameScoreBuilder
							{...common_props}
							nav_active={`/gamescore`}
							exclude_cases={['out_of_moves']}
						>
							<MineSweeperClient
								stage={{ Stage:stage }}
								promotion={props.promotion}
								is_builder={true}
								section="score"
							/>
						</GameScoreBuilder>
					</Route>

					<Route exact={true} path="/test">
						<GameTest
							{...common_props}
							nav_active={`/test`}
						/>
					</Route>
				</Switch>
			</Router>
		</BuilderMain>
	)
}
export default injectIntl(MineSweeperBuilder);